import {
  AnalyzeExperienceFrontendTinybirdRequestBodyType,
  AnalyzeSitewideFrontendTinybirdRequestBodyType,
  AssetTypeType,
  DeviceType,
} from "@intelligems/ig-types";

export const organizationsKeys = {
  all: [{ scope: "organizations" }] as const,
  one: [{ scope: "organizations" }] as const,
  orgSummary: (orgId: string) =>
    [{ scope: "organizations", orgSummary: true, orgId }] as const,
  orgSummaries: [{ scope: "organizations", orgSummaries: true }] as const,
  stripeSubscriptionLinks: [
    { scope: "organizations", stripeSubscriptionLinks: true },
  ] as const,
  billingUrl: (orgId: string) => [
    { ...organizationsKeys.all[0], billingUrl: orgId },
  ],
  webhooks: (orgId: string) =>
    [{ scopes: "organizations", orgId, webhooks: true }] as const,
  scriptTagEnabled: (orgId: string) => [
    { ...organizationsKeys.all[0], scriptTagEnabled: orgId },
  ],
  shopPages: (orgId: string) => [
    { ...organizationsKeys.all[0], orgId, pages: true },
  ],
  cartTransforms: (orgId: string) => [
    { ...organizationsKeys.all[0], orgId, cartTransforms: true },
  ],
  metafield: (
    orgId: string,
    params?: { type: string; id: string; key: string }
  ) => [{ ...organizationsKeys.all[0], orgId, metafield: true, ...params }],
} as const;

export const pluginPerformanceKeys = {
  one: (orgId: string) => [{ scope: "pluginPerformance", orgId }],
} as const;

export const adminKeys = {
  agencies: [{ scope: "admin", agencies: true }] as const,
  agency: (id: string) =>
    [{ scope: "admin", agencies: true, type: "singleAgency", id }] as const,
  meterLogs: (subscriptionLinkId: string) =>
    [{ scope: "admin", meterLogs: true, subscriptionLinkId }] as const,
  agencyCustomers: (agencyId: string) =>
    [{ scope: "admin", agencyCustomers: true, agencyId }] as const,
  organizationAgencyLink: (agencyId: string) =>
    [{ scope: "admin", organizationAgencyLink: true, agencyId }] as const,
  stripeSubscriptionLinks: (orgId: string) =>
    [{ scope: "admin", stripeSubscriptionLinks: true, orgId }] as const,
} as const;

export const intelligemsKeys = {
  livePrices: [{ scope: "intelligems", livePrices: true }] as const,
} as const;

export const pluginPendingTestKeys = {
  one: (testId: string) => [{ scope: "pendingPluginTest", testId }],
} as const;

export const userKeys = {
  all: [{ scope: "user" }] as const,
  hubspotToken: ["hubspotToken"],
};

export const subscriptionProductKeys = {
  all: [{ scope: "subscriptionProducts" }] as const,
  one: (orgId: string) => [{ scope: "subscriptionProducts", orgId }],
} as const;

export const processesKeys = {
  all: [{ scope: "processes" }] as const,
  one: (orgId: string) => [{ scope: "processes", orgId }],
};

export const screenshotKeys = {
  all: [{ scope: "screenshot" }] as const,
  fresh: () => [{ ...screenshotKeys.all[0], fresh: true }],
  org: (orgId: string) => [{ ...screenshotKeys.all[0], orgId }],
  one: (orgId: string, path: string, device: DeviceType, fresh: boolean) => [
    { ...screenshotKeys.all[0], path, orgId, device, fresh },
  ],
};

export const subscriptionsKeys = {
  all: (orgId: string) => [{ scope: "activeSubscriptions", orgId }] as const,
};

export const organizationDomainKey = {
  one: (orgId: string) => [{ scope: "organizationDomain", orgId }] as const,
};

export const organizationProductKey = {
  one: (orgId: string, sample: string) =>
    [{ scope: "organizationProduct", orgId, sample }] as const,
};

export const organizationPageViewsKey = {
  one: (orgId: string) => [{ scope: "organizationPageViews", orgId }] as const,
};

export const organizationScriptStatusKey = {
  org: (orgId: string) =>
    [{ scope: "organizationScriptStatus", orgId }] as const,
  preview: (orgId: string, previewId?: string, fullUrl?: string) =>
    [
      { ...organizationScriptStatusKey.org(orgId)[0], previewId, fullUrl },
    ] as const,
};

export const organizationThemesKey = {
  one: (orgId: string) => [{ scope: "organizationThemes", orgId }] as const,
};

export const organizationThemeAssetsKey = {
  all: [{ scope: "organizationThemeAssets" }] as const,
  one: (orgId: string, themeId: string, assetType: AssetTypeType) =>
    [
      {
        ...organizationThemeAssetsKey.all[0],
        orgId,
        themeId: themeId || "live",
        assetType,
      },
    ] as const,
};

export const historyKeys = {
  one: (id: string) => [{ scope: "history", id }] as const,
  allConfigEvents: [{ scope: "configEvents" }] as const,
  configEvents: (startTs?: number, endTs?: number) =>
    [{ ...historyKeys.allConfigEvents, startTs, endTs }] as const,
  getConfigCompare: (from: string, to: string) =>
    [{ scope: "configCompare", from, to }] as const,
};

export const gaKeys = {
  all: [{ scope: "ga" }] as const,
  accounts: () => [{ ...gaKeys.all[0], accounts: true }],
  streams: (accountId: string, propertyId: string) => [
    { ...gaKeys.all[0], streams: true, accountId, propertyId },
  ],
  audiences: (accountId: string, propertyId: string) => [
    { ...gaKeys.all[0], audiences: true, accountId, propertyId },
  ],
};

export const integrationKeys = {
  all: [{ scope: "integrations" }] as const,
};

export const recommendedProductsKey = {
  product: (productId: string) =>
    [
      {
        scope: "recommendedProducts",
        productId,
      },
    ] as const,
};

export const experienceKeys = {
  experimentList: [{ scope: "experiences-list-experiment" }] as const,
  personalizationList: [{ scope: "experiences-list-personalization" }] as const,
  all: [{ scope: "experiences" }] as const,
  one: (experienceId: string) =>
    [
      {
        scope: "experience",
        experienceId: experienceId,
      },
    ] as const,
};

export const customEventsKeys = {
  all: [{ scope: "customEvents" }] as const,
  one: (eventId: string) =>
    [
      {
        scope: "customEvents",
        eventId: eventId,
      },
    ] as const,
};

export const combinedCustomEventsKeys = {
  all: [{ scope: "combinedCustomEvents" }] as const,
  one: (eventId: string) =>
    [
      {
        scope: "combinedCustomEvents",
        eventId: eventId,
      },
    ] as const,
};

export const experienceKeyMetricsKeys = {
  one: (experienceId: string) =>
    [{ scope: "experienceKeyMetrics", experienceId }] as const,
};

export const scheduleKeys = {
  all: [{ scope: "schedules" }] as const,
  one: (experienceId: string) =>
    [
      {
        scope: "schedules",
        experienceId: experienceId,
      },
    ] as const,
};

export const exclusionGroupKeys = {
  all: [{ scope: "exclusionGroups" }] as const,
  one: (exclusionGroupId: string) =>
    [{ scope: "exclusionGroup", exclusionGroupId }] as const,
};

export const experienceProductsKeys = {
  all: [{ scope: "experienceProducts" }] as const,
  one: (experienceId: string) =>
    [
      {
        scope: "experienceProduct",
        experienceId: experienceId,
      },
    ] as const,
};

export const analyticsProductsKeys = {
  all: [{ scope: "analyticsProducts" }] as const,
  one: (experienceId: string) =>
    [
      {
        scope: "analyticsProduct",
        experienceId: experienceId,
      },
    ] as const,
};

export const aiKeys = {
  all: [{ scope: "ai" }] as const,
  one: (
    orgId: string,
    type:
      | "screenshot-recommendations"
      | "screenshot-recommendations-history"
      | `experience-analysis-${string}`
      | `experience-analysis-history-${string}`
      | "onsite-injections"
  ) => [{ scope: "ai", orgId, type }],
  contextCategories: [
    { scope: "ai", context: true, categories: true },
  ] as const,
  contextFile: (category: string, name: string) =>
    [{ ...aiKeys.contextCategories, category, name }] as const,
  tools: [{ scope: "ai", tools: true }],
};

export const workflowKeys = {
  all: [{ scope: "workflow" }] as const,
  one: (sessionId: string) => [{ ...workflowKeys.all[0], sessionId }],
  sessionSteps: (sessionId: string) =>
    [{ ...workflowKeys.all[0], sessionId, steps: true }] as const,
};

export const discountKeys = {
  all: [{ scope: "discounts" }] as const,
  selected: (discountId: string) =>
    [{ ...discountKeys.all[0], discountId: discountId }] as const,
};

export const shippingProfileBackupKeys = {
  all: [{ scope: "shippingProfileBackups" }] as const,
  selected: (experienceId: string) =>
    [{ ...shippingProfileBackupKeys.all[0], experienceId }] as const,
};

export const userInterfaceKeys = {
  all: [{ scope: "userInterfaces" }] as const,
  selected: (variationId: string) => [
    { ...userInterfaceKeys.all[0], userInterfaceId: variationId },
  ],
};

export const widgetKeys = {
  all: [{ scope: "widgets" }] as const,
  selected: (widgetId: string) =>
    [{ ...widgetKeys.all[0], widgetId: widgetId }] as const,
};

export const orgListKeys = {
  all: [{ scope: "orgList" }] as const,
};

export const offerKeys = {
  all: [{ scope: "offers" }] as const,
};

export const tinybirdEvents = {
  all: [{ scope: "tinybirdEvents" }] as const,
  summaryMetrics: [
    { scope: "tinybirdEvents", type: "summaryMetrics" },
  ] as const,
};

export const unregisteredTinybirdEvents = {
  all: [{ scope: "unregisteredTinybirdEvents" }] as const,
};

export const sitewideAnalyticsKeys = {
  all: (data: AnalyzeSitewideFrontendTinybirdRequestBodyType) =>
    [
      {
        type: "sitewideAnalytics",
        ...dashboardKeys.all[0],
        ...data,
      },
    ] as const,
};

export const dashboardKeys = {
  all: [{ topic: "dashboards" }] as const,

  selectedTinybirdExperiment: (
    data: AnalyzeExperienceFrontendTinybirdRequestBodyType
  ) =>
    [
      {
        selected: true,
        type: "experiencesTinybird",
        ...dashboardKeys.all[0],
        ...data,
      },
    ] as const,
};
